import type {
  AutomationConfig,
  AutomationsClient,
  AutomationsGetCredentialResponse,
  CreateActionTestRequest,
  CreateActionTestResponse,
  CreateAutomationRequest,
  CreateAutomationResponse,
  CreateInputsCredentialRequest,
  CreateInputsCredentialResponse,
  CreateOauthCredentialRequest,
  CreateOauthCredentialResponse,
  CreateSmartSuiteCredentialRequest,
  CreateSmartSuiteCredentialResponse,
  Credential,
  DeleteUiCredentialRequest,
  DynamicActionDescriptionRequest,
  DynamicActionDescriptionResponse,
  DynamicTriggerDescriptionRequest,
  DynamicTriggerDescriptionResponse,
  Empty,
  GetActionTestRequest,
  GetActionTestResponse,
  IntegrationDescription,
  Job,
  JobDetailsRequest,
  JobDetailsResponse,
  ListApplicationsResponse,
  ListAutomationsRequest,
  ListAutomationsResponse,
  ListCredentialsRequest,
  ListCredentialsResponse,
  ListIntegrationsResponse,
  ListJobsRequest,
  ListJobsResponse,
  ListMembersRequest,
  ListMembersResponse,
  ListSolutionsRequest,
  ListSolutionsResponse,
  ListSolutionTeamsAndMembersRequest,
  ListSolutionTeamsAndMembersResponse,
  ListTeamsRequest,
  ListTeamsResponse,
  RetryJobRequest,
  RetryJobResponse,
  UpdateAutomationRequest,
  UpdateAutomationResponse,
  AutomationsGetCredentialRequest,
  GetBranchDataRequest,
  GetBranchDataResponse,
  GetLoopItemDataRequest,
  GetLoopItemDataResponse,
  RecordJobDetailsRequest,
  RecordJobDetailsResponse,
  CredentialDynamicInputsDescriptionRequest,
  CredentialDynamicInputsDescriptionResponse,
} from "@smartsuite/automation-engine-api";

import {
  AutomationsClientJSON,
  AutomationsClientProtobuf,
} from "@smartsuite/automation-engine-api";
import type { FetchRPCOptions } from "@smartsuite-foundry/twirp-ts";
import { FetchRPC } from "@smartsuite-foundry/twirp-ts";
import React from "react";
import { observer } from "mobx-react-lite";
import { useRootStore } from "../stores/hooks";
import { localStorageClient } from "./local-storage-client";

/** Job from ListJobs response without some fields */
export type LimitedJob = Pick<
  Job,
  | "jobId"
  | "status"
  | "triggerTime"
  | "finishTime"
  | "usedActions"
  | "retryStatus"
>;

/** ListJobsResponse with each job some fields omitted */
type LimitedListJobsResponse = Omit<ListJobsResponse, "jobs"> & {
  jobs: LimitedJob[];
};

export class EngineClient {
  constructor(private readonly client: AutomationsClient) {}

  async getAutomationList(
    solutionId: string,
    pageSize: number,
    pageToken: string,
  ): Promise<ListAutomationsResponse> {
    const request: ListAutomationsRequest = {
      solutionId,
      pageSize,
      pageToken,
    };
    const res = await this.client.ListAutomations(request);

    const moveActionsToGroup = (
      automation: AutomationConfig,
    ): AutomationConfig => {
      if (
        automation.actionGroups.length > 0 &&
        automation.actions.length === 0
      ) {
        return automation;
      }

      return {
        ...automation,
        actions: [],
        actionGroups: [
          {
            instanceId: 0,
            config: {
              oneofKind: "actions",
              actions: {
                actions: automation.actions,
              },
            },
          },
        ],
      };
    };

    return {
      ...res,
      automations: res.automations.map(moveActionsToGroup),
    };
  }

  async getDynamicActionDescription(
    request: DynamicActionDescriptionRequest,
  ): Promise<DynamicActionDescriptionResponse> {
    return await this.client.DynamicActionDescription(request);
  }

  async getDynamicTriggerDescription(
    request: DynamicTriggerDescriptionRequest,
  ): Promise<DynamicTriggerDescriptionResponse> {
    return await this.client.DynamicTriggerDescription(request);
  }

  async getIntegrationsList(
    pageSize: number,
    pageToken: string,
  ): Promise<IntegrationDescription[]> {
    const res: ListIntegrationsResponse = await this.client.ListIntegrations({
      pageSize,
      pageToken,
    });
    return res.integrations.filter(
      (i) => i.systemStatus?.systemStatus.oneofKind !== "disabled",
    );
  }

  async createAutomation(
    request: CreateAutomationRequest,
  ): Promise<CreateAutomationResponse> {
    return await this.client.CreateAutomation(request);
  }

  async updateAutomation(
    request: UpdateAutomationRequest,
  ): Promise<UpdateAutomationResponse> {
    return await this.client.UpdateAutomation(request);
  }

  async deleteAutomation(automationId: string): Promise<Empty> {
    return await this.client.DeleteAutomation({ automationId });
  }

  async getCredentialById(
    request: AutomationsGetCredentialRequest,
  ): Promise<AutomationsGetCredentialResponse> {
    return await this.client.GetCredential(request);
  }

  async getAllCredentialList(
    request: ListCredentialsRequest,
  ): Promise<Credential[]> {
    return await this.getNextCredentialPage([], request);
  }

  async getNextCredentialPage(
    prevCredentialList: Credential[],
    request: ListCredentialsRequest,
  ): Promise<Credential[]> {
    const { credentials, nextPageToken } =
      await this.getCredentialPage(request);
    const nextCredentialList = [...prevCredentialList, ...credentials];

    return nextPageToken === ""
      ? nextCredentialList
      : await this.getNextCredentialPage(nextCredentialList, {
          ...request,
          pageToken: nextPageToken,
        });
  }

  async getCredentialPage(
    request: ListCredentialsRequest,
  ): Promise<ListCredentialsResponse> {
    return await this.client.ListCredentials(request);
  }

  async createSmartSuiteCredential(
    request: CreateSmartSuiteCredentialRequest,
  ): Promise<CreateSmartSuiteCredentialResponse> {
    const cachedCred =
      localStorageClient.getItem<CreateSmartSuiteCredentialResponse>(
        "smartsuite-cred",
        (cachedCred) =>
          cachedCred.credential?.solutionId === request.solutionId &&
          cachedCred.credential.authenticationMethodReference?.integrationId ===
            request.authenticationMethodReference?.integrationId,
      );

    if (cachedCred !== undefined) {
      return { credential: cachedCred.credential };
    }

    const response = await this.client.CreateSmartSuiteCredential(request);
    localStorageClient.setItem(
      "smartsuite-cred",
      response,
      5 * 604800000 /* ttl 5 weeks */,
    );
    return response;
  }

  async createOauthCredential(
    request: CreateOauthCredentialRequest,
  ): Promise<CreateOauthCredentialResponse> {
    return await this.client.CreateOauthCredential(request);
  }

  async createInputsCredential(
    request: CreateInputsCredentialRequest,
  ): Promise<CreateInputsCredentialResponse> {
    return await this.client.CreateInputsCredential(request);
  }

  async createParagonAuthCredential(
    request: CreateOauthCredentialRequest,
  ): Promise<CreateOauthCredentialResponse> {
    return await this.client.CreateOauthCredential(request);
  }

  async deleteCredential(request: DeleteUiCredentialRequest): Promise<Empty> {
    return await this.client.DeleteCredential(request);
  }

  async getJobList(request: ListJobsRequest): Promise<LimitedListJobsResponse> {
    return await this.client.ListJobs(request);
  }

  async getJobDetails(request: JobDetailsRequest): Promise<JobDetailsResponse> {
    return await this.client.JobDetails(request);
  }

  async retryJob(request: RetryJobRequest): Promise<RetryJobResponse> {
    return await this.client.RetryJob(request);
  }

  async getSolutions(
    request: ListSolutionsRequest,
  ): Promise<ListSolutionsResponse> {
    return await this.client.ListSolutions(request);
  }

  async getApplications(solutionId: string): Promise<ListApplicationsResponse> {
    return await this.client.ListApplications({ solutionId });
  }

  async listMembers(request: ListMembersRequest): Promise<ListMembersResponse> {
    return await this.client.ListMembers(request);
  }

  async listTeams(request: ListTeamsRequest): Promise<ListTeamsResponse> {
    return await this.client.ListTeams(request);
  }

  async listSolutionTeamsAndMembers(
    request: ListSolutionTeamsAndMembersRequest,
  ): Promise<ListSolutionTeamsAndMembersResponse> {
    return await this.client.ListSolutionTeamsAndMembers(request);
  }

  async createTestAction(
    request: CreateActionTestRequest,
  ): Promise<CreateActionTestResponse> {
    return await this.client.CreateActionTest(request);
  }

  async getTestAction(
    request: GetActionTestRequest,
  ): Promise<GetActionTestResponse> {
    return await this.client.GetActionTest(request);
  }

  async getRecordJobDetails(
    request: RecordJobDetailsRequest,
  ): Promise<RecordJobDetailsResponse> {
    return await this.client.RecordJobDetails(request);
  }

  async getLoopItemData(
    request: GetLoopItemDataRequest,
  ): Promise<GetLoopItemDataResponse> {
    return await this.client.GetLoopItemData(request);
  }

  async getBranchData(
    request: GetBranchDataRequest,
  ): Promise<GetBranchDataResponse> {
    return await this.client.GetBranchData(request);
  }

  async getCredentialDynamicInputsDescription(
    request: CredentialDynamicInputsDescriptionRequest,
  ): Promise<CredentialDynamicInputsDescriptionResponse> {
    return await this.client.CredentialDynamicInputsDescription(request);
  }
}

const EngineClientContext = React.createContext(
  new EngineClient(
    new (window.sessionStorage.JSON_RPC === "1"
      ? AutomationsClientJSON
      : AutomationsClientProtobuf)(
      FetchRPC({
        baseUrl: new URL("/", window.location.href).href,
        credentials: "include",
      }),
    ),
  ),
);

export const useEngineClient = (): EngineClient =>
  React.useContext(EngineClientContext);

export const EngineClientProvider = observer<{
  children: React.ReactNode;
}>(({ children }) => {
  const rootStore = useRootStore();
  const { accountId, accessToken, engineUrl } = rootStore.config;

  // Memoized so the client's identity is stable across provider re-renders:
  // hooks keying effects on the client (useLazyOutputs' fetchers) would
  // otherwise cancel and refire in-flight requests whenever an ancestor
  // re-renders.
  const engineClient = React.useMemo(() => {
    const headers: FetchRPCOptions["headers"] = {
      "account-id": accountId,
    };

    if (accessToken !== undefined) {
      headers["access-token"] = accessToken;
    }

    return new EngineClient(
      new (window.sessionStorage.JSON_RPC === "1"
        ? AutomationsClientJSON
        : AutomationsClientProtobuf)(
        FetchRPC({
          baseUrl: engineUrl,
          headers,
          credentials: "include",
        }),
      ),
    );
  }, [accountId, accessToken, engineUrl]);

  return (
    <EngineClientContext.Provider value={engineClient}>
      {children}
    </EngineClientContext.Provider>
  );
});
